import { useEffect, useRef, useCallback, useState } from 'react';

export interface BarcodeScanResult {
  barcode: string;
  timestamp: number;
}

interface UseBarcodeScanner {
  /** Whether the scanner is currently capturing a barcode sequence */
  isScanning: boolean;
  /** The last successfully scanned barcode */
  lastScan: BarcodeScanResult | null;
  /** Whether scanner detection is active */
  enabled: boolean;
  /** Toggle scanner on/off */
  setEnabled: (enabled: boolean) => void;
  /** Ref to attach to hidden input for iOS scanner capture */
  inputRef: React.RefObject<HTMLInputElement | null>;
}

// Bluetooth HID scanners can have 50-100ms inter-char delays; USB is <30ms.
// 150ms threshold safely catches Bluetooth while still filtering human typing (>200ms).
const SCANNER_CHAR_THRESHOLD_MS = 150;
// Process or discard buffer after this timeout (covers Enter-less scanners)
const BUFFER_TIMEOUT_MS = 300;
// Minimum barcode length to be considered valid
const MIN_BARCODE_LENGTH = 3;
// Debounce for hidden input (iOS) — scanner sends all chars within ~100ms
const HIDDEN_INPUT_DEBOUNCE_MS = 200;

/**
 * Hook that detects Socket Mobile S720 (and other keyboard-wedge) barcode scanners.
 *
 * Uses TWO INDEPENDENT detection methods for maximum compatibility:
 * 1. document keydown listener (works on desktop browsers where no input is focused)
 * 2. hidden input field (required for iPad/iOS PWAs where keydown doesn't fire on document)
 *
 * IMPORTANT: These methods are mutually exclusive. The document keydown handler
 * SKIPS events from the hidden input to prevent double-processing. The hidden input
 * uses its own simple buffer since ALL input to it is scanner data (no timing detection needed).
 */
export function useBarcodeScanner(
  onScan: (barcode: string) => void
): UseBarcodeScanner {
  const [isScanning, setIsScanning] = useState(false);
  const [lastScan, setLastScan] = useState<BarcodeScanResult | null>(null);
  const [enabled, setEnabled] = useState(true);

  // Refs for Method 1 (document keydown — desktop)
  const bufferRef = useRef('');
  const lastKeyTimeRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scanningRef = useRef(false);

  // Refs for Method 2 (hidden input — iOS)
  const hiddenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onScanRef = useRef(onScan);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Keep callback ref up to date without re-subscribing listener
  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  // Shared: emit a successful scan
  const emitScan = useCallback((barcode: string) => {
    const trimmed = barcode.trim();
    if (trimmed.length >= MIN_BARCODE_LENGTH) {
      playBeep();
      const result: BarcodeScanResult = { barcode: trimmed, timestamp: Date.now() };
      setLastScan(result);
      setIsScanning(false);
      onScanRef.current(trimmed);
    }
  }, []);

  // ─── Method 1: document keydown (desktop browsers) ───
  const clearDesktopBuffer = useCallback(() => {
    bufferRef.current = '';
    scanningRef.current = false;
    setIsScanning(false);
  }, []);

  const processDesktopBarcode = useCallback((barcode: string) => {
    emitScan(barcode);
    bufferRef.current = '';
    scanningRef.current = false;
  }, [emitScan]);

  const handleDesktopBufferTimeout = useCallback(() => {
    if (scanningRef.current && bufferRef.current.length >= MIN_BARCODE_LENGTH) {
      processDesktopBarcode(bufferRef.current);
    } else {
      clearDesktopBuffer();
    }
  }, [processDesktopBarcode, clearDesktopBuffer]);

  const handleDesktopChar = useCallback((char: string, now: number) => {
    const timeSinceLastKey = now - lastKeyTimeRef.current;

    if (char === 'Enter' || char === '\n') {
      if (scanningRef.current && bufferRef.current.length >= MIN_BARCODE_LENGTH) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        processDesktopBarcode(bufferRef.current);
      } else {
        clearDesktopBuffer();
      }
      return;
    }

    if (bufferRef.current.length === 0) {
      bufferRef.current = char;
      lastKeyTimeRef.current = now;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(handleDesktopBufferTimeout, BUFFER_TIMEOUT_MS);
      return;
    }

    if (timeSinceLastKey < SCANNER_CHAR_THRESHOLD_MS) {
      if (!scanningRef.current) {
        scanningRef.current = true;
        setIsScanning(true);
      }
      bufferRef.current += char;
      lastKeyTimeRef.current = now;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(handleDesktopBufferTimeout, BUFFER_TIMEOUT_MS);
    } else {
      clearDesktopBuffer();
      bufferRef.current = char;
      lastKeyTimeRef.current = now;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(handleDesktopBufferTimeout, BUFFER_TIMEOUT_MS);
    }
  }, [processDesktopBarcode, clearDesktopBuffer, handleDesktopBufferTimeout]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const tagName = target.tagName.toLowerCase();

      // SKIP events from the hidden scanner input — Method 2 handles those
      if (target === inputRef.current) return;

      // Don't capture if user is typing in a visible input
      if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
        if (!scanningRef.current) return;
      }

      if (e.key === 'Enter') {
        if (scanningRef.current && bufferRef.current.length >= MIN_BARCODE_LENGTH) {
          e.preventDefault();
          e.stopPropagation();
        }
        handleDesktopChar('Enter', Date.now());
        return;
      }

      if (e.key.length !== 1) return;

      if (scanningRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }

      handleDesktopChar(e.key, Date.now());
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [enabled, handleDesktopChar]);

  // ─── Method 2: hidden input for iOS/iPad ───
  // ALL input to the hidden field is scanner data (it's invisible to the user).
  // Simple approach: let iOS accumulate text in the input naturally,
  // then read the complete value after a debounce.
  useEffect(() => {
    if (!enabled) return;
    const input = inputRef.current;
    if (!input) return;

    const processInput = () => {
      const barcode = input.value.replace(/[\r\n]/g, '').trim();
      input.value = '';
      setIsScanning(false);
      if (barcode.length >= MIN_BARCODE_LENGTH) {
        playBeep();
        const result: BarcodeScanResult = { barcode, timestamp: Date.now() };
        setLastScan(result);
        onScanRef.current(barcode);
      }
    };

    const handleInput = () => {
      // Don't clear input.value — let characters accumulate naturally.
      // Just reset the debounce timer each time new input arrives.
      setIsScanning(true);
      if (hiddenTimeoutRef.current) clearTimeout(hiddenTimeoutRef.current);
      hiddenTimeoutRef.current = setTimeout(processInput, HIDDEN_INPUT_DEBOUNCE_MS);
    };

    const handleInputKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        // Enter signals end of barcode — process after short delay
        // to let any final characters arrive
        if (hiddenTimeoutRef.current) clearTimeout(hiddenTimeoutRef.current);
        hiddenTimeoutRef.current = setTimeout(processInput, 80);
      }
    };

    // Keep the hidden input focused for iOS scanner capture
    const refocus = () => {
      // Don't steal focus if user has text selected (copy-paste)
      const selection = window.getSelection();
      if (selection && selection.toString().length > 0) return;

      const active = document.activeElement as HTMLElement;
      const activeTag = active?.tagName?.toLowerCase();
      if (active && active !== input && active !== document.body &&
          (activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select')) {
        return; // User is in a real input, don't steal focus
      }
      input.focus({ preventScroll: true });
    };

    input.addEventListener('input', handleInput);
    input.addEventListener('keydown', handleInputKeyDown);

    // Focus initially and refocus periodically
    refocus();
    const focusInterval = setInterval(refocus, 1000);

    // Also refocus on touch end (user tapped somewhere that's not an input)
    const handleTouchEnd = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      const tag = target?.tagName?.toLowerCase();
      if (tag !== 'input' && tag !== 'textarea' && tag !== 'select') {
        setTimeout(refocus, 100);
      }
    };
    document.addEventListener('touchend', handleTouchEnd, true);

    return () => {
      input.removeEventListener('input', handleInput);
      input.removeEventListener('keydown', handleInputKeyDown);
      document.removeEventListener('touchend', handleTouchEnd, true);
      clearInterval(focusInterval);
      if (hiddenTimeoutRef.current) clearTimeout(hiddenTimeoutRef.current);
    };
  }, [enabled, emitScan]);

  return { isScanning, lastScan, enabled, setEnabled, inputRef };
}

/** Play a short beep using Web Audio API */
function playBeep() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.frequency.value = 1200;
    oscillator.type = 'sine';
    gain.gain.value = 0.3;
    oscillator.start();
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    oscillator.stop(ctx.currentTime + 0.15);
  } catch {
    // Audio not available — silently ignore
  }
}
